import DataQuery from "data/DataQuery";
import { Customer_ConsumptionHistory, Customer_ConsumptionHistorylExport, PlaceOfDelivery_ContractsDuration } from "data/endpoints";
import { useQuery } from "data/hooks/useQuery";
import IFileService from "data/IFileService";
import PlaceOfDeliveryConsumptionSimplified from "features/placeOfDelivery/components/PlaceOfDeliveryConsumptionSimplified";
import PlaceOfDeliveryConsumptionSimplifiedHeader from "features/placeOfDelivery/components/PlaceOfDeliveryConsumptionSimplifiedHeader";
import DataGrid from "features/shared/components/dataGrid/DataGrid";
import { DataGridId } from "features/shared/components/dataGrid/DataGridId";
import NoContentWrapper from "features/shared/components/noContentWrapper/NoContentWrapper";
import { loadDataFilterConfig } from "features/shared/helpers/modules";
import { getPlaceOfDeliveryContractsDurationParams } from "features/shared/helpers/queryParams";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import CustomerState from "infrastructure/store/customer/CustomerState";
import IAppState from "infrastructure/store/IAppState";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { CommodityDetail } from "features/contracts/enums/commodityDetail";
import ConsumptionHistoryNoContentAlert from "features/consumption/components/ConsumptionHistoryNoContentAlert";
import MeasuredDataHistoryHeader from "features/shared/components/measuredData/MeasuredDataHistoryHeader";
import { useTranslation } from "react-i18next";

const consumptionHistoryDataFilterJson = loadDataFilterConfig(
  "consumptionHistoryDataFilter"
);

type Props = {
  isProduction: boolean;
};

const ConsumptionHistorySection = ({ isProduction }: Props) => {
  // DI
  const fileService = useInjection<IFileService>(IocTypes.FileService);

  // translations
  const tLabels = useTranslation("consumption\\consumption").t;

  // store
  const customer = useSelector<IAppState, CustomerState>( (store) => store.customer).customer;
  const consumptionFilterParams = useSelector<IAppState, string | undefined>(
    (state) => state.dataGrid.gridSettings[DataGridId.consumptionHistoryGrid]?.filtrUrlSearchParams
  );

  // state
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedCommodities, setSelectedCommodities] = useState<CommodityDetail[] | undefined>(undefined);

  // fetch
  const contractsDurationQuery: DataQuery = {
    url: PlaceOfDelivery_ContractsDuration,
    params: getPlaceOfDeliveryContractsDurationParams(customer.customerId, undefined, selectedCommodities, isProduction)
  };
  const { data: years, loadStatus: yearsLoadStatus } = useQuery<number[]>(
    contractsDurationQuery,
    [],
    [selectedCommodities],
    { useTracker: true },
    { apply: true, condition: () => selectedCommodities !== undefined }
  );

  // side-effects
  useEffect(() => {
    if (years.length > 0) {
      if (!years.includes(selectedYear)) {
        setSelectedYear(years[years.length - 1]);
      }
    }
  }, [years]);

  useEffect(() => {
    if (consumptionFilterParams) {
      let filterParams = new URLSearchParams(consumptionFilterParams);
      let filterCommodities = filterParams.getAll("CommodityDetail");
      setSelectedCommodities(filterCommodities as CommodityDetail[]);
    } else {
      setSelectedCommodities([]);
    }
  }, [consumptionFilterParams])
  

  // helpers
  const getPlaceOfDeliveryConsumptionParams = (consumptionFilterParams?: string) => { 
    if (years.length > 0) {
      let params = getPlaceOfDeliveryContractsDurationParams();
      params.set("Year", selectedYear.toString());
      params.set("IsProduction", `${isProduction}`);

      if (consumptionFilterParams) {
        let filterParams = new URLSearchParams(consumptionFilterParams);

        for (const [key, value] of filterParams.entries()) {
          params.append(key, value);
        }
      }

      return params;
    }
  }

  // handlers
  const downloadConsumptionExcelHandler = () => {
    let query: DataQuery = {
      url: Customer_ConsumptionHistorylExport,
      routeParams: getRouteParams("customerId", customer.customerId!),
      params: getPlaceOfDeliveryConsumptionParams(consumptionFilterParams)
    };
    
    fileService.downloadFile(query);
  }

  // sub-renders
  const renderDataGridHeader = (count: number, filterActive?: boolean, setShowFilterModal?: React.Dispatch<React.SetStateAction<boolean>>) => { 
    return (
      <MeasuredDataHistoryHeader 
        years={years}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        count={count}
        countLabel={tLabels("consumptionHistoryDataGridHeader.placeOfDeliveryCount")}
        filterActive={filterActive!}
        setShowFilterModal={setShowFilterModal!}
        onDownloadExcelClick={downloadConsumptionExcelHandler}
      />
    );
  }

  // render
  return (
    <NoContentWrapper
      data={years}
      loadStatus={yearsLoadStatus}
      noContentRender={() => <ConsumptionHistoryNoContentAlert />}
    >
      <DataGrid
        gridId={DataGridId.consumptionHistoryGrid}
        url={Customer_ConsumptionHistory}
        filterJson={consumptionHistoryDataFilterJson}
        renderItem={(item) => (
          <PlaceOfDeliveryConsumptionSimplified consumption={item} />
        )}
        renderHeader={() => <PlaceOfDeliveryConsumptionSimplifiedHeader isProduction={isProduction} />}
        noFilterQueryParams={getPlaceOfDeliveryConsumptionParams()}
        renderCustomGridHeader={renderDataGridHeader}
        externalDependencies={[selectedYear]}
      />
    </NoContentWrapper>
  );
};

export default ConsumptionHistorySection;
